import { useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "convex/_generated/api";
import { useNavigate } from "@tanstack/react-router";
import {
  Inbox,
  ClipboardCheck,
  FileText,
  FileBarChart,
  FileCheck,
  FlaskConical,
  ShieldCheck,
  LayoutDashboard,
  BookOpen,
  Zap,
} from "lucide-react";
import { useTranslation } from "@/packages/utils";
import type {
  SidebarProps,
  RecentAudit,
  TeamMember,
} from "@/components/sidebar";

interface UseSidebarDataReturn {
  sidebarProps: SidebarProps;
  isLoading: boolean;
}

export function useSidebarData(workspaceSlug: string): UseSidebarDataReturn {
  const { t } = useTranslation(["sidebar"]);
  const navigate = useNavigate();
  const prefix = workspaceSlug ? `/${workspaceSlug}` : "";

  const handleCreateWorkspace = useCallback(() => {
    navigate({ to: "/onboarding", search: { new: true } });
  }, [navigate]);

  const { data: currentUserData, isLoading: currentUserLoading } = useQuery(
    convexQuery(api.users.getCurrent, {}),
  );

  const { data: workspacesData } = useQuery(
    convexQuery(api.workspaces.getMyWorkspaces, {}),
  );
  const currentWorkspace = workspacesData?.find(
    (ws) => ws.slug === workspaceSlug,
  );

  const { data: audits, isLoading: auditsLoading } = useQuery(
    convexQuery(
      api.audit_queries.listAudits,
      currentWorkspace?._id ? { workspaceId: currentWorkspace._id } : "skip",
    ),
  );

  const { data: workspaceMembers, isLoading: workspaceMembersLoading } =
    useQuery(
      convexQuery(
        api.workspaces.getMembers,
        currentWorkspace?._id ? { workspaceId: currentWorkspace._id } : "skip",
      ),
    );

  const { data: unreadData } = useQuery(
    convexQuery(api.notifications.queries.index.unreadCount, {}),
  );

  const recentAudits: RecentAudit[] = [...(audits ?? [])]
    .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
    .slice(0, 5)
    .map((audit) => ({
      id: audit.id,
      name: audit.displayName,
      icon: FileCheck,
      iconColor: "#3B82F6",
      to: `${prefix}/audits/${audit.id}`,
    }));

  const userName = currentUserData?.name ?? currentUserData?.email ?? "User";
  const workspaceColor = currentWorkspace?.accentColor ?? "var(--color-accent)";

  const teamMembers: TeamMember[] = (workspaceMembers ?? []).map((member) => ({
    name: member.userName,
    avatarColor: workspaceColor,
  }));

  const isSuperUser = currentUserData?.isSuperUser === true;
  const currentUser = currentUserData
    ? {
        name: userName,
        email: currentUserData.email ?? "",
        avatarUrl: (currentUserData as Record<string, unknown>).image as
          | string
          | undefined,
        avatarColor: workspaceColor,
        workspaceId: currentWorkspace?._id,
      }
    : undefined;

  const workspaceInfo = currentWorkspace
    ? {
        name: currentWorkspace.name ?? "Unknown Workspace",
        initials: currentWorkspace.name?.slice(0, 2).toUpperCase() ?? "??",
        color: currentWorkspace.accentColor ?? "var(--color-accent)",
        currentSlug: workspaceSlug,
        onNew: handleCreateWorkspace,
      }
    : {
        name: "...",
        initials: "..",
        color: "var(--color-accent)",
        currentSlug: workspaceSlug,
        onNew: handleCreateWorkspace,
      };

  const sidebarProps: SidebarProps = {
    workspace: workspaceInfo,
    primaryNav: [
      {
        icon: LayoutDashboard,
        label: t("sidebar:nav.dashboard"),
        to: `${prefix}/dashboard`,
      },
      {
        icon: Inbox,
        label: t("sidebar:nav.inbox"),
        to: `${prefix}/inbox`,
        badge: unreadData?.count ? unreadData.count : undefined,
      },
      {
        icon: ClipboardCheck,
        label: t("sidebar:nav.audits"),
        to: `${prefix}/audits`,
      },
      {
        icon: FileText,
        label: t("sidebar:nav.documents"),
        to: `${prefix}/documents`,
      },
      {
        icon: FileBarChart,
        label: t("sidebar:nav.reports"),
        to: `${prefix}/reports`,
      },
      {
        icon: BookOpen,
        label: t("sidebar:nav.myStandards"),
        to: `${prefix}/standards`,
      },
      ...(isSuperUser
        ? [
            {
              icon: FlaskConical,
              label: t("sidebar:nav.playground"),
              to: "/dev",
            },
          ]
        : []),
    ],
    recentAudits,
    teamMembers,
    bottomNav: isSuperUser
      ? [
          {
            icon: ShieldCheck,
            label: t("sidebar:nav.standards"),
            to: `${prefix}/admin/standards`,
          },
          {
            icon: Zap,
            label: t("sidebar:nav.adminActions"),
            to: `${prefix}/admin/actions`,
          },
        ]
      : [],
    currentUser,
  };

  return {
    sidebarProps,
    isLoading: auditsLoading || workspaceMembersLoading || currentUserLoading,
  };
}
